import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { MPPDetail } from '@/libs/fleet-api/services/mpp';
import { PrintDisbursementSubmitVerificationPayload } from '@/libs/fleet-api/services/print-disbursement';
import { Col, Divider, Form, Input, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';

export type CovenantData =
	PrintDisbursementSubmitVerificationPayload['covenants'][number] &
		MPPDetail['covenants'][number];

export const useCovenantModal = () =>
	useAsyncModal<
		CovenantData,
		PrintDisbursementSubmitVerificationPayload['covenants'][number]
	>();

export type CovenantModalProps = ReturnType<typeof useCovenantModal> & {
	covenantStatusOptions: MasterData[];
};

const CovenantModal: FC<CovenantModalProps> = ({
	modal,
	close,
	data,
	covenantStatusOptions,
}) => {
	const { form, fieldName } = useForm<CovenantData>();

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="CANCEL"
			okText="SAVE"
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (isOpen && data) {
					form.setFieldsValue(data);
				} else {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Edit Data Covenant</FleetTitle>
				</div>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={close}
				>
					<Form.Item
						name={fieldName('id')}
						noStyle
					/>
					<Form.Item
						label="Covenant"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						{data?.covenant ?? '-'}
					</Form.Item>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Kategori">
								{data?.category?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Priority">
								{data?.priority?.value ?? '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Repeat">
								{data?.repeatFrequency?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Jenis Monitoring">
								{data?.monitoringType?.value ?? '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Analisa Lebih Lanjut">
								{data?.furtherAnalysis?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Tanggal Upload">
								{data?.uploadDocumentDate
									? dayjs(data.uploadDocumentDate).format('DD/MM/YYYY')
									: '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={20}>
						<Col span={12}>
							<Form.Item label="Tanggal Upload Dokumen">
								{data?.uploadDocumentDate
									? dayjs(data.uploadDocumentDate).format('DD/MM/YYYY')
									: '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('status')}
								label="Status"
								rules={[rule.required()]}
							>
								<Select
									options={covenantStatusOptions.map((option) => ({
										label: option.value,
										value: option.id,
									}))}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Due Date">
								{data?.dueDate ? dayjs(data.dueDate).format('DD/MM/YYYY') : '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="OD">
								{data?.overdueDays ? `${data.overdueDays} Hari` : '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Form.Item
						label="Keterangan"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						<Input.TextArea
							rows={4}
							disabled
							value={data?.notes}
						/>
					</Form.Item>
					<Divider />
					<Form.Item
						name={fieldName('additionalInformation')}
						rules={[rule.required()]}
						label="Informasi Tambahan"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						<Input.TextArea rows={4} />
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default CovenantModal;
